import { useState } from 'react';
import {
  Avatar,
  Button,
  Pagination,
  Segmented,
  Space,
  Input,
  Typography,
  theme,
  type TableProps,
} from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { DataTable } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';
import { useServiciosList, useServiciosStats } from '../api/useServicios';
import { useCatalogos } from '../../catalogos/api/useCatalogos';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useT } from '../../../locales';
import { useServiciosFilters } from '../hooks/useServiciosFilters';
import { paginationProps } from '../../../lib/paginacion';
import { ServiciosKpis } from './ServiciosKpis';
import { groupServicios, type ServiciosGroupBy } from '../servicios-agrupacion';
import { buildServiciosColumns } from './servicios-columnas';
import { getInitials } from '../../../lib/initials';
import { CatalogoSelect } from '../../../components/CatalogoSelect';
import { PlanSelect } from '../../../components/PlanSelect';
import { TipoServicioSelect } from '../../../components/TipoServicioSelect';
import { ServicioCreateModal } from './ServicioCreateModal';
import { ServicioDetailModal } from './ServicioDetailModal';
import { ServicioEditModal } from './ServicioEditModal';
import { CATEGORIA_ESTADO_SERVICIO, Permission, type ServicioResponse } from '@aid/shared';
import { parseServerSorter, type ServerSortState } from '../../../lib/server-sort';

const { Text } = Typography;

export function ServiciosTable() {
  const t = useT();
  const { token } = theme.useToken();
  const filters = useServiciosFilters();
  const pageSize = 20;
  const [groupBy, setGroupBy] = useState<ServiciosGroupBy>('none');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [sort, setSort] = useState<ServerSortState>({});

  const queryFilters = {
    search: filters.searchDebounced || undefined,
    estado: filters.estado,
    idPlan: filters.idPlan,
    idTipoServicio: filters.idTipoServicio,
  };

  const { data, isLoading, error } = useServiciosList({
    limit: pageSize,
    offset: (filters.page - 1) * pageSize,
    ...queryFilters,
    ...sort,
  });

  const { data: stats, isLoading: statsLoading } = useServiciosStats(queryFilters);

  const { data: estadosCatalogo } = useCatalogos('ESTADO_SERVICIO');
  const canCreate = useHasPermission(Permission.SERVICIOS_CREATE);
  const canUpdate = useHasPermission(Permission.SERVICIOS_UPDATE);

  const rows = data?.data ?? [];
  const caption = filters.hasFilters ? t('common.segunFiltros') : undefined;
  const grupos = groupServicios(rows, groupBy);

  if (error) {
    return <EmptyState title={t('pages.servicios.errorLoad')} />;
  }

  const columns = buildServiciosColumns({
    t,
    token,
    canUpdate,
    onVer: (id) => setDetailId(id),
    onEditar: (id) => setEditId(id),
    groupBy,
    sort,
  });

  const handleTableChange: NonNullable<TableProps<ServicioResponse>['onChange']> = (
    pagination,
    _filters,
    sorter,
  ) => {
    const nextSort = parseServerSorter(sorter);
    if (nextSort.sortBy !== sort.sortBy || nextSort.sortOrder !== sort.sortOrder) {
      setSort(nextSort);
      filters.setPage(1);
      return;
    }
    filters.setPage(pagination.current ?? 1);
  };

  return (
    <div>
      <ServiciosKpis
        stats={stats}
        statsLoading={statsLoading}
        caption={caption}
        estadosCatalogo={estadosCatalogo ?? []}
      />
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <Space wrap size={[8, 8]} className="table-filters">
          <Text type="secondary">{t('common.groupBy')}</Text>
          <Segmented
            options={[
              { label: t('common.groupByNone'), value: 'none' },
              { label: t('common.groupByCliente'), value: 'cliente' },
              { label: t('common.groupByEstado'), value: 'estado' },
            ]}
            value={groupBy}
            onChange={(v) => {
              setGroupBy(v as ServiciosGroupBy);
              filters.setPage(1);
            }}
          />
          <Input
            placeholder={t('pages.servicios.searchPlaceholder')}
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => filters.setSearch(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
          <div style={{ width: 180 }}>
            <CatalogoSelect
              category={CATEGORIA_ESTADO_SERVICIO}
              placeholder={t('pages.servicios.filtroEstado')}
              value={filters.estado}
              onChange={filters.setEstado}
            />
          </div>
          <div style={{ width: 180 }}>
            <PlanSelect
              placeholder={t('pages.servicios.filtroPlan')}
              value={filters.idPlan}
              onChange={filters.setIdPlan}
            />
          </div>
          <div style={{ width: 180 }}>
            <TipoServicioSelect
              placeholder={t('pages.servicios.filtroTipo')}
              value={filters.idTipoServicio}
              onChange={filters.setIdTipoServicio}
            />
          </div>
        </Space>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            {t('pages.servicios.create')}
          </Button>
        )}
      </div>
      {grupos ? (
        <>
          {grupos.map(([label, filas]) => (
            <div key={label} style={{ marginBottom: 24 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  marginBottom: 8,
                  borderRadius: 8,
                  background: token.colorFillTertiary,
                  border: `1px solid ${token.colorBorderSecondary}`,
                }}
              >
                {groupBy === 'cliente' && (
                  <Avatar
                    size={24}
                    style={{ background: token.colorPrimary, fontSize: 12 }}
                    aria-hidden
                  >
                    {getInitials(filas[0]?.cliente?.nombre)}
                  </Avatar>
                )}
                <Text strong>{label}</Text>
                <Text type="secondary">{filas.length}</Text>
              </div>
              <DataTable<ServicioResponse>
                columns={columns}
                dataSource={filas}
                rowKey="id"
                pagination={false}
                onChange={handleTableChange}
              />
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <Pagination
              current={filters.page}
              pageSize={pageSize}
              total={data?.total ?? 0}
              showSizeChanger={false}
              showTotal={(total, range) =>
                t('pagination.showTotal', { from: range[0], to: range[1], total })
              }
              onChange={(p) => filters.setPage(p)}
            />
          </div>
        </>
      ) : (
        <DataTable<ServicioResponse>
          columns={columns}
          dataSource={data?.data}
          rowKey="id"
          loading={isLoading}
          locale={{
            emptyText: (
              <EmptyState
                title={t('pages.servicios.emptyTitle')}
                description={t('pages.servicios.emptyDescription')}
              />
            ),
          }}
          pagination={paginationProps(t, {
            page: filters.page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: false,
          })}
          onChange={handleTableChange}
        />
      )}
      <ServicioCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      {detailId !== null && <ServicioDetailModal id={detailId} onClose={() => setDetailId(null)} />}
      {editId !== null && <ServicioEditModal id={editId} onClose={() => setEditId(null)} />}
    </div>
  );
}
